import React, { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';
import {
  PREVIEW_SHEET_BREAKPOINT,
  PREVIEW_SHEET_BREAKPOINTS,
} from '@/modules/config';
import { ContactLogic } from '@/modules/contact';
import { OutboxLogic } from './logic';
import OutboxMenu, { type OutboxMenuAction } from './OutboxMenu';
import type { OutboxItem, OutboxRowStatus } from './types';
import styles from './OutboxPreviewSheet.module.css';

/**
 * The exact message one outbox row sends, over the outbox (mock 9r). Its title
 * is the message's intent and the row's status; who it goes to is a To: row of
 * chips that wraps as a group grows, ruled off from the message.
 *
 * Each row is exactly one message, so there is nothing to choose between here.
 * The ⋯ in the header offers the row's actions, the same as a long-press on
 * the row, for anyone who does not think to long-press (mock 9s). There is no
 * close button: the sheet is dragged down by its handle.
 */
const OutboxPreviewSheet: React.FC<{
  item: OutboxItem | null;
  status: OutboxRowStatus | null;
  actions: readonly OutboxMenuAction[];
  onDismiss: () => void;
}> = ({ item, status, actions, onDismiss }) => {
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);

  return (
    <IonModal
      isOpen={item != null}
      initialBreakpoint={PREVIEW_SHEET_BREAKPOINT}
      breakpoints={PREVIEW_SHEET_BREAKPOINTS}
      expandToScroll={false}
      handle
      onDidDismiss={() => {
        setMenuAnchor(null);
        onDismiss();
      }}
      data-testid="outbox-preview-sheet"
    >
      {item && status && (
        <>
          <IonHeader>
            <IonToolbar>
              <IonTitle data-testid="outbox-preview-title">
                {OutboxLogic.sheetTitle(item.leg.intent, status)}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  aria-label="Actions"
                  data-testid="outbox-preview-more"
                  onClick={event =>
                    setMenuAnchor(
                      (
                        event.currentTarget as HTMLElement
                      ).getBoundingClientRect()
                    )
                  }
                >
                  <IonIcon slot="icon-only" icon={ellipsisHorizontal} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className={styles.to}>
              <span className={styles.toLabel}>To:</span>
              <ul className={styles.chips}>
                {item.recipients.map(contact => (
                  <li key={contact.localId} className={styles.chip}>
                    {ContactLogic.displayName(contact)}
                  </li>
                ))}
              </ul>
            </div>
            <hr className={styles.rule} />
            <p className={styles.message} data-testid="outbox-preview-message">
              {OutboxLogic.messageFor(item)}
            </p>
          </IonContent>
          <OutboxMenu
            anchor={menuAnchor}
            actions={actions}
            align="end"
            onDismiss={() => setMenuAnchor(null)}
            data-testid="outbox-preview-menu"
          />
        </>
      )}
    </IonModal>
  );
};

export default OutboxPreviewSheet;
